import React, { useState, useEffect, useMemo } from 'react';
import { ConsultantNode } from '../../types/career';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { formatAE } from '../../utils/formatters';
import { getStoredUser, AuthUser } from '../../services/auth';
import {
  SavedStructure,
  getSavedStructures,
  fetchCloudStructures,
  saveStructureToCloud,
  deleteStructureFromCloud,
  groupStructuresByFolder,
} from '../../services/structureLibrary';
import { useSimulationStore } from '../../store/useSimulationStore';
import {
  BookmarkCheck,
  BookmarkPlus,
  Search,
  Trash2,
  FolderOpen,
  Folder,
  ChevronDown,
  ChevronRight,
  Clock,
  Rocket,
  Sparkles,
  Cloud,
  RefreshCw,
} from 'lucide-react';

export interface StructureLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTree: ConsultantNode;
  onLoadStructure: (tree: ConsultantNode, title?: string) => void;
  onSimulateStructure?: (tree: ConsultantNode) => void;
  currentUser?: AuthUser | null;
}

export const StructureLibraryModal: React.FC<StructureLibraryModalProps> = ({
  isOpen,
  onClose,
  currentTree,
  onLoadStructure,
  onSimulateStructure,
  currentUser: propUser,
}) => {
  const [structures, setStructures] = useState<SavedStructure[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({});

  const activeUser = propUser || getStoredUser();
  const activeStructureTitle = useSimulationStore((state) => state.activeStructureTitle);

  const syncWithCloud = async () => {
    setIsSyncing(true);
    try {
      const cloudList = await fetchCloudStructures(activeUser?.accountId);
      setStructures(cloudList);
    } catch (e) {
      console.warn('Cloud sync error:', e);
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      // 1. Instant render from account-isolated local cache
      setStructures(getSavedStructures(activeUser?.accountId));
      const defaultTitle = activeStructureTitle || `${currentTree.name} (Р${currentTree.level})`;
      setNewTitle(defaultTitle);
      setSaveSuccess(false);

      // 2. Fetch and sync latest from Cloudflare KV
      syncWithCloud();
    }
  }, [isOpen, currentTree, activeUser?.accountId, activeStructureTitle]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return structures;
    return structures.filter(
      (s) =>
        s.consultantName.toLowerCase().includes(q) ||
        (s.customTitle && s.customTitle.toLowerCase().includes(q))
    );
  }, [structures, searchQuery]);

  const folders = useMemo(() => groupStructuresByFolder(filtered), [filtered]);

  if (!isOpen) return null;

  const handleSaveCurrent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim() || isSaving) return;

    setIsSaving(true);
    try {
      await saveStructureToCloud(currentTree, newTitle.trim());
      setStructures(getSavedStructures(activeUser?.accountId));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch {
      // Local fallback handled by service
      setStructures(getSavedStructures(activeUser?.accountId));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Ви дійсно бажаєте видалити цю структуру з хмарної бібліотеки?')) {
      const updated = await deleteStructureFromCloud(id);
      setStructures(updated);
    }
  };

  const handleOpenInSandbox = (item: SavedStructure) => {
    onLoadStructure(item.tree, item.customTitle || item.consultantName);
    onClose();
  };

  const handleSimulate = (item: SavedStructure, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSimulateStructure) {
      onSimulateStructure(item.tree);
      onClose();
    }
  };

  const toggleFolder = (folderTitle: string) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [folderTitle]: prev[folderTitle] !== undefined ? !prev[folderTitle] : false,
    }));
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('uk-UA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={
        <div className="flex items-center gap-2">
          <BookmarkCheck className="w-5 h-5 text-accent-emerald" />
          <span>Бібліотека збережених структур</span>
        </div>
      }
      subtitle="Хмарне сховище Cloudflare KV: ваші структури згруповані по папках із історією версій"
    >
      <div className="space-y-5">
        {/* Cloud Status Banner */}
        <div className="flex items-center justify-between px-3.5 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-xs text-text-secondary">
          <div className="flex items-center gap-2">
            <Cloud className="w-4 h-4 text-accent-neon" />
            <span>
              Хмарне сховище для:{' '}
              <strong className="text-white font-medium">
                {activeUser ? activeUser.name : 'Поточний консультант'}
              </strong>
            </span>
          </div>

          <button
            type="button"
            onClick={syncWithCloud}
            disabled={isSyncing}
            className="flex items-center gap-1.5 text-accent-neon hover:underline cursor-pointer text-[11px]"
            title="Оновити з хмари Cloudflare KV"
          >
            <RefreshCw className={`w-3 h-3 ${isSyncing ? 'animate-spin' : ''}`} />
            <span>{isSyncing ? 'Синхронізація...' : 'Оновити'}</span>
          </button>
        </div>

        {/* Quick Save Box for Currently Active Tree */}
        <div className="glass-panel rounded-2xl p-4 border border-accent-emerald/30 bg-gradient-to-r from-emerald-950/30 to-slate-900/60 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-accent-neon uppercase tracking-wider flex items-center gap-1.5">
              <BookmarkPlus className="w-4 h-4" />
              Зберегти поточну структуру в хмару
            </span>
            <span className="text-[11px] text-text-muted">
              Лідер: <strong className="text-white">{currentTree.name}</strong> (Р{currentTree.level})
            </span>
          </div>

          <form onSubmit={handleSaveCurrent} className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Назва структури або папки..."
              className="
                flex-1 px-3 py-2 text-base sm:text-xs rounded-xl bg-slate-950/80
                border border-white/15 focus:border-accent-emerald text-white
                placeholder:text-text-muted focus:outline-none min-h-[38px]
              "
            />
            <Button
              type="submit"
              variant="primary"
              size="sm"
              leftIcon={<BookmarkCheck className="w-4 h-4" />}
              loading={isSaving}
              disabled={isSaving || !newTitle.trim()}
            >
              {saveSuccess ? 'Збережено в хмару!' : 'Зберегти версію'}
            </Button>
          </form>

          {saveSuccess && (
            <div className="text-[11px] text-accent-neon flex items-center gap-1 animate-fadeIn">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Структуру збережено як нову версію в хмарному сховищі!</span>
            </div>
          )}
        </div>

        {/* Search Bar for Saved Items */}
        {structures.length > 0 && (
          <div className="relative">
            <Search className="w-4 h-4 text-text-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Пошук у бібліотеці за ім'ям або назвою папки..."
              className="
                w-full pl-9 pr-4 py-2 text-base sm:text-xs rounded-xl bg-slate-900/80
                border border-white/10 text-white placeholder:text-text-muted focus:outline-none
                focus:border-accent-emerald/40 min-h-[38px]
              "
            />
          </div>
        )}

        {/* List of Saved Structure Folders with Versions */}
        <div className="space-y-3 max-h-[50vh] overflow-y-auto custom-scrollbar pr-1">
          {structures.length === 0 ? (
            <div className="glass-card rounded-2xl p-8 text-center border border-dashed border-slate-700 space-y-2">
              <BookmarkPlus className="w-10 h-10 text-text-muted mx-auto stroke-1" />
              <div className="text-sm font-semibold text-text-primary">Бібліотека порожня</div>
              <p className="text-xs text-text-muted max-w-sm mx-auto">
                Створіть або налаштуйте структуру в конструкторі та збережіть її за допомогою форми вище.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-xs text-text-muted">
              Не знайдено жодної структури за запитом «{searchQuery}»
            </div>
          ) : (
            folders.map((folder) => {
              const isExpanded = expandedFolders[folder.folderTitle] !== false; // Default expanded
              return (
                <div
                  key={folder.folderTitle}
                  className="glass-card rounded-2xl border border-white/10 overflow-hidden transition-all"
                >
                  {/* Folder Header */}
                  <div
                    onClick={() => toggleFolder(folder.folderTitle)}
                    className="
                      p-3.5 bg-slate-900/90 hover:bg-slate-800/80 transition-colors cursor-pointer
                      flex items-center justify-between gap-3 select-none
                    "
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
                        {isExpanded ? <FolderOpen className="w-4 h-4" /> : <Folder className="w-4 h-4" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-bold text-sm text-text-primary hover:text-accent-emerald transition-colors truncate">
                            {folder.folderTitle}
                          </h4>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 font-mono">
                            {folder.versions.length}{' '}
                            {folder.versions.length === 1
                              ? 'версія'
                              : folder.versions.length < 5
                              ? 'версії'
                              : 'версій'}
                          </span>
                          <Badge level={folder.latestVersion.level as any} size="sm">
                            Р{folder.latestVersion.level}
                          </Badge>
                        </div>
                        <div className="text-[11px] text-text-muted flex items-center gap-2 mt-0.5">
                          <span>Останнє збереження: {formatDate(folder.latestVersion.savedAt)}</span>
                          <span>•</span>
                          <span>{folder.latestVersion.teamSize} конс.</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<FolderOpen className="w-3.5 h-3.5 text-cyan-400" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenInSandbox(folder.latestVersion.raw);
                        }}
                        className="text-xs hidden sm:flex"
                      >
                        Остання версія
                      </Button>
                      <button
                        type="button"
                        className="p-1.5 rounded-lg text-text-muted hover:text-white hover:bg-white/5 transition-colors"
                        aria-label={isExpanded ? 'Згорнути папку' : 'Розгорнути папку'}
                      >
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Versions inside Folder */}
                  {isExpanded && (
                    <div className="p-3 space-y-2 bg-slate-950/40 border-t border-white/5">
                      {folder.versions.map((ver, idx) => {
                        const isLatest = idx === 0;
                        return (
                          <div
                            key={ver.id}
                            onClick={() => handleOpenInSandbox(ver.raw)}
                            className="
                              p-2.5 sm:p-3 rounded-xl bg-slate-900/60 hover:bg-slate-800/80 border border-white/5
                              hover:border-accent-emerald/30 transition-all cursor-pointer flex flex-col sm:flex-row
                              sm:items-center justify-between gap-2.5 group
                            "
                          >
                            <div className="min-w-0 space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span
                                  className={`text-[11px] font-mono px-1.5 py-0.5 rounded font-bold ${
                                    isLatest
                                      ? 'bg-emerald-500/20 text-accent-neon border border-emerald-500/30'
                                      : 'bg-slate-800 text-text-secondary border border-white/10'
                                  }`}
                                >
                                  v{ver.versionNumber} {isLatest ? '(остання)' : ''}
                                </span>
                                <Badge level={ver.level as any} size="sm">
                                  Р{ver.level}
                                </Badge>
                                <span className="text-xs text-text-secondary">
                                  {ver.teamSize} конс.
                                </span>
                                <span className="text-xs font-mono font-bold text-accent-neon">
                                  {formatAE(ver.totalMonthlyAE, 0)}/міс
                                </span>
                              </div>

                              <div className="text-[11px] text-text-muted flex items-center gap-1">
                                <Clock className="w-3 h-3 text-text-muted" />
                                <span>{formatDate(ver.savedAt)}</span>
                              </div>
                            </div>

                            <div className="flex items-center justify-end gap-1.5 shrink-0 pt-1 sm:pt-0 border-t sm:border-t-0 border-white/5">
                              {onSimulateStructure && (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  leftIcon={<Rocket className="w-3.5 h-3.5 text-accent-neon" />}
                                  onClick={(e) => handleSimulate(ver.raw, e)}
                                >
                                  Симулювати
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                leftIcon={<FolderOpen className="w-3.5 h-3.5 text-cyan-400" />}
                                onClick={() => handleOpenInSandbox(ver.raw)}
                              >
                                Відкрити
                              </Button>
                              <button
                                type="button"
                                onClick={(e) => handleDelete(ver.id, e)}
                                className="p-1.5 rounded-lg text-text-muted hover:text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                                title="Видалити цю версію"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
};
